<template>
  <div class="formulario-inscripcion">
    <div class="card-body pt-4 pb-15 px-4">
      <div class="row g-4">
        <!-- Documentación -->
        <template v-if="showDocumentacionForm">
          <!-- Nacionalidad -->
          <div
            v-if="viaje.formularioInscripcion.checkIn.nacionalidadEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.nacionalidad') }}</label>
            <div class="mt-auto w-100">
              <InputSelector
                v-model="formularioData.formDataDocumentacion.nacionalidad"
                :options="countryList"
                :has-error="submitted && errors.nacionalidad"
                :class="{ 'is-field-error': submitted && errors.nacionalidad }"
                :placeholder="$t('reserva.checkIn.placeholders.seleccionaNacionalidad')"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.nacionalidad" class="text-danger sub">{{ errors.nacionalidad }}</small>
          </div>

          <!-- Fecha Nacimiento -->
          <div
            v-if="viaje.formularioInscripcion.checkIn.fechaNacimientoEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.fechaNacimiento') }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.fechaNacimiento"
                :class="{ 'is-invalid': submitted && errors.fechaNacimiento }"
                :locale="locale"
                auto-apply
                model-type="yyyy-MM-dd"
                :format="DATE_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar']"
                :placeholder="$t('reserva.checkIn.placeholders.fecha')"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.fechaNacimiento" class="text-danger sub">{{
              errors.fechaNacimiento
            }}</small>
          </div>

          <!-- Lugar Nacimiento -->
          <div
            v-if="viaje.formularioInscripcion.checkIn.lugarNacimientoEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.lugarNacimiento') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.lugarNacimiento"
                :class="{ 'is-invalid': errors.lugarNacimiento }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.LUGAR_NACIMIENTO)" />
            </div>
            <small v-if="errors.lugarNacimiento" class="text-danger sub">{{ errors.lugarNacimiento }}</small>
          </div>

          <!-- Sexo -->
          <div v-if="viaje.formularioInscripcion.checkIn.sexoEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.sexo.title') }}</label>
            <div class="mt-auto w-100">
              <div class="d-flex flex-column gap-2">
                <div class="form-check">
                  <input
                    :id="'sexo-hombre' + formularioData.id"
                    v-model="formularioData.formDataDocumentacion.sexo"
                    class="form-check-input"
                    type="radio"
                    value="hombre"
                    :disabled="!formulario.checkInPendiente" />
                  <label class="form-check-label" :for="'sexo-hombre' + formularioData.id">
                    {{ $t('reserva.checkIn.sexo.hombre') }}
                  </label>
                </div>
                <div class="form-check">
                  <input
                    :id="'sexo-mujer' + formularioData.id"
                    v-model="formularioData.formDataDocumentacion.sexo"
                    class="form-check-input"
                    type="radio"
                    value="mujer"
                    :disabled="!formulario.checkInPendiente" />
                  <label class="form-check-label" :for="'sexo-mujer' + formularioData.id">
                    {{ $t('reserva.checkIn.sexo.mujer') }}
                  </label>
                </div>
              </div>
            </div>
            <small v-if="submitted && errors.sexo" class="text-danger sub">{{ errors.sexo }}</small>
          </div>

          <!-- Pais -->
          <div v-if="viaje.formularioInscripcion.checkIn.paisEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.pais') }}</label>
            <div class="mt-auto w-100">
              <InputSelector
                v-model="formularioData.formDataDocumentacion.pais"
                :options="countryList"
                :has-error="submitted && errors.pais"
                :class="{ 'is-field-error': submitted && errors.pais }"
                :placeholder="$t('reserva.checkIn.placeholders.seleccionaPais')"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.pais" class="text-danger sub">{{ errors.pais }}</small>
          </div>

          <!-- tipoDocumento -->
          <div v-if="showTipoDocumentoSelector" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.tipoDocumento') }}</label>
            <div class="mt-auto w-100">
              <InputSelector
                v-model="formularioData.formDataDocumentacion.tipoDocumento"
                :options="tiposDocumentoOptions"
                :has-error="submitted && errors.tipoDocumento"
                :class="{ 'is-field-error': submitted && errors.tipoDocumento }"
                :placeholder="$t('reserva.checkIn.placeholders.seleccionaTipoDocumento')"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.tipoDocumento" class="text-danger sub">{{ errors.tipoDocumento }}</small>
          </div>

          <!-- Disclaimer documentos -->
          <div
            v-if="showDniFields || showPasaporteFields"
            class="col-12 d-flex align-items-center gap-2 aviso-documentos">
            <CustomIcon :icon="infoIcon" size="12px" class="text-landing flex-shrink-0 opacity-80" />
            <span class="small text-landing opacity-80 medium">{{ $t('reserva.checkIn.aviso.datosOficiales') }}</span>
          </div>

          <!-- DNI / documento segun nacionalidad -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.dniEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <div class="d-flex align-items-center gap-1 mb-2">
              <label class="form-label text-landing small mb-0">{{ $t('reserva.checkIn.documentoIdentidad') }}</label>
              <InfoIcon size="12px" class="opacity-80 flex-shrink-0" :message-info="documentoTooltip" />
            </div>
            <div class="mt-auto w-100">
              <input
                :value="formularioData.formDataDocumentacion.dni"
                :class="{ 'is-invalid': dniErrorMessage }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="dniPlaceholder"
                @input="formularioData.formDataDocumentacion.dni = formatDocumentoInput($event)"
                @blur="validateField(CAMPOS_FORMULARIO.DNI)" />
            </div>
            <small v-if="dniErrorMessage" class="text-danger sub">
              {{ dniErrorMessage }}
            </small>
          </div>

          <!-- numSoporte DNI -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.numSoporteDniEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.numSoporte') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.numSoporteDni"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                placeholder="AAA000000" />
            </div>
          </div>

          <!-- Fecha Caducidad DNI -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.fechaCaducidadDniEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.fechaCaducidadDni') }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.fechaCaducidadDni"
                :locale="locale"
                auto-apply
                model-type="yyyy-MM-dd"
                :format="DATE_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar']"
                placeholder="dd/mm/aa"
                :disabled="!formulario.checkInPendiente" />
            </div>
          </div>

          <!-- Fecha Expedición DNI -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.fechaExpedicionDniEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.fechaExpedicionDni') }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.fechaExpedicionDni"
                :locale="locale"
                auto-apply
                model-type="yyyy-MM-dd"
                :format="DATE_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar']"
                placeholder="dd/mm/aa"
                :disabled="!formulario.checkInPendiente" />
            </div>
          </div>

          <!-- Anverso DNI -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.anversoDniEnabled && formulario.checkInPendiente"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.anversoDni') }}</label>
            <div class="mt-auto w-100">
              <div class="file-input-wrapper">
                <button
                  v-if="!formularioData.formDataDocumentacion.anversoDni"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.anversoDni,
                  }"
                  @click="anversoDniInput.click()">
                  <span>{{ $t('reserva.checkIn.seleccionarDocumento') }}</span>
                  <UploadIcon class="ms-1" />
                </button>
                <button
                  v-if="formularioData.formDataDocumentacion.anversoDni"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.anversoDni,
                  }"
                  @click="fileDeletedHandler('anversoDni', anversoDniInput)">
                  <span>{{ formularioData.formDataDocumentacion.anversoDni.name }}</span>
                  <i class="fas fa-trash"></i>
                </button>
                <input
                  ref="anversoDniInput"
                  type="file"
                  accept="image/jpeg, image/png"
                  class="d-none"
                  :disabled="!formulario.checkInPendiente"
                  @change="fileChangeHandler($event, 'anversoDni')" />
              </div>
            </div>
            <small v-if="submitted && errors.anversoDni" class="text-danger sub">
              {{ errors.anversoDni }}
            </small>
          </div>

          <!-- Reverso DNI -->
          <div
            v-if="showDniFields && viaje.formularioInscripcion.checkIn.reversoDniEnabled && formulario.checkInPendiente"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.reversoDni') }}</label>
            <div class="mt-auto w-100">
              <div class="file-input-wrapper">
                <button
                  v-if="!formularioData.formDataDocumentacion.reversoDni"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.reversoDni,
                  }"
                  @click="reversoDniInput.click()">
                  <span>{{ $t('reserva.checkIn.seleccionarDocumento') }}</span>
                  <UploadIcon class="ms-1" />
                </button>
                <button
                  v-if="formularioData.formDataDocumentacion.reversoDni"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.reversoDni,
                  }"
                  @click="fileDeletedHandler('reversoDni', reversoDniInput)">
                  <span>{{ formularioData.formDataDocumentacion.reversoDni.name }}</span>
                  <i class="fas fa-trash"></i>
                </button>
                <input
                  ref="reversoDniInput"
                  type="file"
                  accept="image/jpeg, image/png"
                  class="d-none"
                  :disabled="!formulario.checkInPendiente"
                  @change="fileChangeHandler($event, 'reversoDni')" />
              </div>
            </div>
            <small v-if="submitted && errors.reversoDni" class="text-danger sub">
              {{ errors.reversoDni }}
            </small>
          </div>

          <!-- Pasaporte -->
          <div
            v-if="showPasaporteFields && viaje.formularioInscripcion.checkIn.pasaporteEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.pasaporte') }}</label>
            <div class="mt-auto w-100">
              <input
                :value="formularioData.formDataDocumentacion.pasaporte"
                :class="{ 'is-invalid': pasaporteErrorMessage }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="pasaportePlaceholder"
                @input="formularioData.formDataDocumentacion.pasaporte = formatDocumentoInput($event)"
                @blur="validateField(CAMPOS_FORMULARIO.PASAPORTE)" />
            </div>
            <small v-if="pasaporteErrorMessage" class="text-danger sub">{{ pasaporteErrorMessage }}</small>
          </div>

          <!-- numSoporte Pasaporte -->
          <div
            v-if="showPasaporteFields && viaje.formularioInscripcion.checkIn.numSoportePasaporteEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.numSoportePasaporte') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.numSoportePasaporte"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                placeholder="00000000" />
            </div>
          </div>

          <!-- Fecha Caducidad Pasaporte -->
          <div
            v-if="showPasaporteFields && viaje.formularioInscripcion.checkIn.fechaCaducidadPasaporteEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{
              $t('reserva.checkIn.fechaCaducidadPasaporte')
            }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.fechaCaducidadPasaporte"
                :locale="locale"
                auto-apply
                model-type="yyyy-MM-dd"
                :format="DATE_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar']"
                placeholder="dd/mm/aa"
                :disabled="!formulario.checkInPendiente" />
            </div>
          </div>

          <!-- Fecha Expedición Pasaporte -->
          <div
            v-if="showPasaporteFields && viaje.formularioInscripcion.checkIn.fechaExpedicionPasaporteEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{
              $t('reserva.checkIn.fechaExpedicionPasaporte')
            }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.fechaExpedicionPasaporte"
                :locale="locale"
                auto-apply
                model-type="yyyy-MM-dd"
                :format="DATE_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar']"
                placeholder="dd/mm/aa"
                :disabled="!formulario.checkInPendiente" />
            </div>
          </div>

          <!-- Anverso Pasaporte -->
          <div
            v-if="
              showPasaporteFields &&
              viaje.formularioInscripcion.checkIn.anversoPasaporteEnabled &&
              formulario.checkInPendiente
            "
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.anversoPasaporte') }}</label>
            <div class="mt-auto w-100">
              <div class="file-input-wrapper">
                <button
                  v-if="!formularioData.formDataDocumentacion.anversoPasaporte"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.anversoPasaporte,
                  }"
                  @click="anversoPasaporteInput.click()">
                  <span>{{ $t('reserva.checkIn.seleccionarDocumento') }}</span>
                  <UploadIcon class="ms-1" />
                </button>
                <button
                  v-if="formularioData.formDataDocumentacion.anversoPasaporte"
                  class="file-input-button"
                  :class="{
                    disabled: !formulario.checkInPendiente,
                    'is-invalid': submitted && errors.anversoPasaporte,
                  }"
                  @click="fileDeletedHandler('anversoPasaporte', anversoPasaporteInput)">
                  <span>{{ formularioData.formDataDocumentacion.anversoPasaporte.name }}</span>
                  <i class="fas fa-trash"></i>
                </button>
                <input
                  ref="anversoPasaporteInput"
                  type="file"
                  accept="image/jpeg, image/png"
                  class="d-none"
                  :disabled="!formulario.checkInPendiente"
                  @change="fileChangeHandler($event, 'anversoPasaporte')" />
              </div>
            </div>
            <small v-if="submitted && errors.anversoPasaporte" class="text-danger sub">
              {{ errors.anversoPasaporte }}
            </small>
          </div>

          <!-- Provincia -->
          <div v-if="viaje.formularioInscripcion.checkIn.provinciaEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.provincia') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.provincia"
                :class="{ 'is-invalid': errors.provincia }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.PROVINCIA)" />
            </div>
            <small v-if="errors.provincia" class="text-danger sub">{{ errors.provincia }}</small>
          </div>

          <!-- Poblacion -->
          <div v-if="viaje.formularioInscripcion.checkIn.poblacionEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.poblacion') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.poblacion"
                :class="{ 'is-invalid': errors.poblacion }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.POBLACION)" />
            </div>
            <small v-if="errors.poblacion" class="text-danger sub">{{ errors.poblacion }}</small>
          </div>

          <!-- Direccion -->
          <div v-if="viaje.formularioInscripcion.checkIn.domicilioEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.direccion') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.direccion"
                :class="{ 'is-invalid': errors.direccion }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.DIRECCION)" />
            </div>
            <small v-if="errors.direccion" class="text-danger sub">{{ errors.direccion }}</small>
          </div>

          <!-- Codigo Postal -->
          <div v-if="viaje.formularioInscripcion.checkIn.codPostalEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.codigoPostal') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataDocumentacion.codigoPostal"
                :class="{ 'is-invalid': errors.codigoPostal }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.checkInPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.CODIGO_POSTAL)" />
            </div>
            <small v-if="errors.codigoPostal" class="text-danger sub">{{ errors.codigoPostal }}</small>
          </div>

          <!-- Llegada -->
          <div v-if="viaje.formularioInscripcion.checkIn.llegadaEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.llegada') }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.horaCheckIn"
                :class="{ 'is-invalid': submitted && errors.llegada }"
                :locale="locale"
                auto-apply
                enable-time-picker
                is24
                model-type="yyyy-MM-dd'T'HH:mm:ss"
                :format="DATETIME_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar', 'time']"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.llegada" class="text-danger sub">{{ errors.llegada }}</small>
          </div>

          <!-- Salida -->
          <div v-if="viaje.formularioInscripcion.checkIn.salidaEnabled" class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.salida') }}</label>
            <div class="mt-auto w-100">
              <Datepicker
                v-model="formularioData.formDataDocumentacion.horaCheckOut"
                :class="{ 'is-invalid': submitted && errors.salida }"
                :locale="locale"
                auto-apply
                enable-time-picker
                is24
                model-type="yyyy-MM-dd'T'HH:mm:ss"
                :format="DATETIME_FORMAT_DISPLAY"
                :flow="['year', 'month', 'calendar', 'time']"
                :disabled="!formulario.checkInPendiente" />
            </div>
            <small v-if="submitted && errors.salida" class="text-danger sub">{{ errors.salida }}</small>
          </div>
        </template>

        <!-- Datos Personales -->
        <template v-if="showPersonalForm">
          <!-- Alergias -->
          <div
            v-if="viaje.formularioInscripcion.datosPersonales.alergiasEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.alergias') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataPersonal.alergias"
                :class="{ 'is-invalid': errors.alergias }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.datosPersonalesPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.ALERGIAS)" />
            </div>
            <small v-if="errors.alergias" class="text-danger sub">{{ errors.alergias }}</small>
          </div>

          <!-- Altura -->
          <div
            v-if="viaje.formularioInscripcion.datosPersonales.alturaEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.altura') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataPersonal.altura"
                type="number"
                step="any"
                min="0"
                :class="{ 'is-invalid': errors.altura }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.datosPersonalesPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.ALTURA)" />
            </div>
            <small v-if="errors.altura" class="text-danger sub">{{ errors.altura }}</small>
          </div>

          <!-- Peso -->
          <div
            v-if="viaje.formularioInscripcion.datosPersonales.pesoEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.peso') }}</label>
            <div class="mt-auto w-100">
              <div class="input-group">
                <input
                  v-model="formularioData.formDataPersonal.peso"
                  type="number"
                  step="any"
                  min="0"
                  :class="{ 'is-invalid': errors.peso }"
                  class="form-control bg-light border-0 py-3"
                  :disabled="!formulario.datosPersonalesPendiente"
                  placeholder="0"
                  @blur="validateField(CAMPOS_FORMULARIO.PESO)" />
                <span class="input-group-text bg-light border-0">Kg</span>
              </div>
            </div>
            <small v-if="errors.peso" class="text-danger sub">{{ errors.peso }}</small>
          </div>

          <!-- Talla de pie -->
          <div
            v-if="viaje.formularioInscripcion.datosPersonales.tallaPieEnabled"
            class="col-12 col-md-6 d-flex flex-column">
            <label class="form-label text-landing small mb-2">{{ $t('reserva.checkIn.tallaPie') }}</label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataPersonal.tallaPie"
                type="number"
                step="any"
                min="0"
                :class="{ 'is-invalid': errors.tallaPie }"
                class="form-control bg-light border-0 py-3"
                :disabled="!formulario.datosPersonalesPendiente"
                :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
                @blur="validateField(CAMPOS_FORMULARIO.TALLA_PIE)" />
            </div>
            <small v-if="errors.tallaPie" class="text-danger sub">{{ errors.tallaPie }}</small>
          </div>
        </template>

        <!-- Preguntas -->
        <template v-if="showPreguntas">
          <div
            v-for="pregunta in viaje.formularioInscripcion.preguntas"
            :key="pregunta.id"
            class="d-flex flex-column text-landing input-container pregunta-input"
            :class="isLongPregunta(pregunta.pregunta) ? 'col-12' : 'col-12 col-md-6'">
            <label class="label-input translate" :for="`pregunta_${pregunta.id}`">
              {{ pregunta.pregunta }}
            </label>
            <div class="mt-auto w-100">
              <input
                v-model="formularioData.formDataPreguntas.preguntas.find(item => item.id === pregunta.id).respuesta"
                :class="{ 'is-invalid': errors.preguntas[pregunta.id] }"
                class="form-control text-start"
                :name="`pregunta_${pregunta.id}`"
                :placeholder="t('subtitulo.escribeRespuesta')"
                type="text"
                :disabled="instant && !formulario.preguntasPendiente"
                @blur="validatePregunta(pregunta.id)" />
            </div>
            <small v-if="errors.preguntas[pregunta.id]" class="text-danger sub">
              {{ errors.preguntas[pregunta.id] }}
            </small>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, watch, onBeforeMount, computed } from 'vue';
  import { getCountryOptions } from '@/helpers/countryUtils';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import debounce from 'lodash/debounce';
  import { cloneDeep } from 'lodash';
  import { useI18n } from 'vue-i18n';
  import UploadIcon from '@/components/Icons/UploadIcon.vue';
  import { comprimirImagen } from '@/helpers/imageHelpers';
  import { createToaster } from '@meforma/vue-toaster';
  import { TIPOS_DOCUMENTO, TIPO_DOCUMENTO_A_PEDIR } from '@/constants/usuarioConstants';
  import { CAMPOS_FORMULARIO } from '@/constants/formularioConstants';
  import InputSelector from '@/components/inputs/InputSelector.vue';
  import Datepicker from '@vuepic/vue-datepicker';
  import { checkDocumento, formatDocumentoInput } from '@/helpers/documentoIdentidadUtils';
  import { DATETIME_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY } from '@/helpers/dateUtiles';
  import infoIcon from '@/assets/icons/raw/info.svg';
  import CustomIcon from '@/components/CustomIcon.vue';
  import InfoIcon from '@/components/Icons/InfoIcon.vue';

  const props = defineProps({
    formulario: {
      type: Object, //RReservaDatosFormularioInscripcionDTO
      required: true,
    },
    instant: {
      type: Boolean,
      default: false,
    },
  });

  const emits = defineEmits(['updateFormulario']);
  const { t, locale } = useI18n();
  const toaster = createToaster({ position: 'top-right' });

  const storeHabitacion = informacionHabitacion();
  const viaje = storeHabitacion.getViaje;
  const countryList = ref({});
  const submitted = ref(false);
  const anversoDniInput = ref(null);
  const reversoDniInput = ref(null);
  const anversoPasaporteInput = ref(null);

  const LONG_PREGUNTA_CHARS = 80;

  const tiposDocumentoList = computed(() => [
    {
      id: TIPOS_DOCUMENTO.DNI,
      nombre: t('reserva.checkIn.tiposDocumento.' + TIPOS_DOCUMENTO.DNI),
    },
    {
      id: TIPOS_DOCUMENTO.PASAPORTE,
      nombre: t('reserva.checkIn.tiposDocumento.' + TIPOS_DOCUMENTO.PASAPORTE),
    },
  ]);

  const tiposDocumentoOptions = computed(() => {
    return tiposDocumentoList.value.reduce((acc, tipo) => {
      acc[tipo.id] = tipo.nombre;
      return acc;
    }, {});
  });

  const showDocumentacionForm = computed(
    () => viaje.formularioInscripcion?.checkInEnabled && (!props.instant || viaje.formularioInscripcion?.checkInInstant)
  );

  const showPersonalForm = computed(
    () =>
      viaje.formularioInscripcion?.datosPersonalesEnabled &&
      (!props.instant || viaje.formularioInscripcion?.datosPersonalesInstant)
  );
  const showPreguntas = computed(
    () =>
      viaje.formularioInscripcion?.preguntasEnabled &&
      (!props.instant || viaje.formularioInscripcion?.preguntasInstant) &&
      viaje.formularioInscripcion?.preguntas &&
      viaje.formularioInscripcion?.preguntas.length > 0
  );

  const showFormulario = computed(() => showDocumentacionForm.value || showPersonalForm.value || showPreguntas.value);

  const showTipoDocumentoSelector = computed(
    () =>
      viaje.formularioInscripcion?.checkIn.tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE &&
      props.formulario?.checkInPendiente
  );

  const showDniFields = computed(() => {
    if (!viaje.formularioInscripcion?.checkInEnabled) return false;
    const tipoDocumentoAPedir = viaje?.formularioInscripcion?.checkIn?.tipoDocumentoAPedir;

    if (!props.formulario?.checkInPendiente && tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE) {
      return !!props.formulario?.checkIn.dniSubido;
    }

    return (
      tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI ||
      tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_AND_PASAPORTE ||
      (tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE &&
        formularioData.formDataDocumentacion.tipoDocumento === TIPOS_DOCUMENTO.DNI)
    );
  });
  const showPasaporteFields = computed(() => {
    if (!viaje.formularioInscripcion?.checkInEnabled) return false;
    const tipoDocumentoAPedir = viaje?.formularioInscripcion?.checkIn?.tipoDocumentoAPedir;

    if (!props.formulario?.checkInPendiente && tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE) {
      return !!props.formulario?.checkIn.pasaporteSubido;
    }

    return (
      tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.PASAPORTE ||
      tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_AND_PASAPORTE ||
      (tipoDocumentoAPedir === TIPO_DOCUMENTO_A_PEDIR.DNI_OR_PASAPORTE &&
        formularioData.formDataDocumentacion.tipoDocumento === TIPOS_DOCUMENTO.PASAPORTE)
    );
  });

  const formularioData = reactive({
    id: props.formulario?.id,
    nombre: props.formulario?.checkIn.nombre,
    showed: showFormulario.value,
    checkInPendiente: props.formulario?.checkInPendiente,
    checkInEnabled: viaje.formularioInscripcion?.checkInEnabled,
    checkInInstant: viaje.formularioInscripcion?.checkInInstant,
    checkIn: viaje.formularioInscripcion?.checkIn,
    dniSubido: props.formulario?.checkIn.dniSubido,
    pasaporteSubido: props.formulario?.checkIn.pasaporteSubido,
    datosPersonalesPendiente: props.formulario?.datosPersonalesPendiente,
    datosPersonalesEnabled: viaje.formularioInscripcion?.datosPersonalesEnabled,
    datosPersonalesInstant: viaje.formularioInscripcion?.datosPersonalesInstant,
    datosPersonales: viaje.formularioInscripcion?.datosPersonales,
    preguntasEnabled: viaje.formularioInscripcion?.preguntasEnabled,
    preguntasInstant: viaje.formularioInscripcion?.preguntasInstant,
    preguntasPendiente: props.formulario?.preguntasPendiente,
    formDataDocumentacion: {
      nombre: props.formulario?.checkIn.nombre || null,
      apellidos: props.formulario?.checkIn.apellidos || null,
      nacionalidad: props.formulario?.checkIn.nacionalidad ?? null,
      fechaNacimiento: props.formulario?.checkIn.fechaNacimiento || null,
      lugarNacimiento: props.formulario?.checkIn.lugarNacimiento || null,
      tipoDocumento: null,
      dni: props.formulario?.checkIn.dni || null,
      numSoporteDni: props.formulario?.checkIn.numSoporteDni || null,
      fechaCaducidadDni: props.formulario?.checkIn.fechaCaducidadDni || null,
      fechaExpedicionDni: props.formulario?.checkIn.fechaExpedicionDni || null,
      pasaporte: props.formulario?.checkIn.pasaporte || null,
      numSoportePasaporte: props.formulario?.checkIn.numSoportePasaporte || null,
      fechaCaducidadPasaporte: props.formulario?.checkIn.fechaCaducidadPasaporte || null,
      fechaExpedicionPasaporte: props.formulario?.checkIn.fechaExpedicionPasaporte || null,
      pais: props.formulario?.checkIn.pais ?? null,
      provincia: props.formulario?.checkIn.provincia || null,
      poblacion: props.formulario?.checkIn.poblacion || null,
      direccion: props.formulario?.checkIn.direccion || null,
      codigoPostal: props.formulario?.checkIn.codigoPostal || null,
      sexo: props.formulario?.checkIn.sexo || null,
      horaCheckIn: props.formulario?.checkIn.horaCheckIn || null,
      horaCheckOut: props.formulario?.checkIn.horaCheckOut || null,
      anversoDni: props.formulario?.checkIn.anversoDni || null,
      reversoDni: props.formulario?.checkIn.reversoDni || null,
      anversoPasaporte: props.formulario?.checkIn.anversoPasaporte || null,
    },
    formDataPersonal: {
      alergias: props.formulario?.datosPersonales.alergias || null,
      altura: props.formulario?.datosPersonales.altura || null,
      peso: props.formulario?.datosPersonales.peso || null,
      tallaPie: props.formulario?.datosPersonales.tallaPie || null,
    },
    formDataPreguntas: {
      preguntas:
        viaje.formularioInscripcion?.preguntas?.map(pregunta => {
          const preguntaExistente = props.formulario?.preguntas?.preguntas?.find(p => p.id === pregunta.id);
          return {
            id: pregunta.id,
            respuesta: preguntaExistente?.respuesta ?? null,
          };
        }) || [],
    },
  });
  const errors = reactive({
    nacionalidad: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    sexo: '',
    tipoDocumento: '',
    dni: '',
    pasaporte: '',
    pais: '',
    provincia: '',
    poblacion: '',
    direccion: '',
    codigoPostal: '',
    llegada: '',
    salida: '',
    anversoDni: '',
    reversoDni: '',
    anversoPasaporte: '',
    alergias: '',
    altura: '',
    peso: '',
    tallaPie: '',
    preguntas: {},
  });

  const documentoTooltip = computed(() => {
    const pais = formularioData.formDataDocumentacion.pais;
    if (!pais) return t('reserva.checkIn.tooltipTiposDocumentoIdentidad');
    return checkDocumento(pais, '').hints;
  });

  const dniPlaceholder = computed(() => checkDocumento(formularioData.formDataDocumentacion.pais, '').example);
  const pasaportePlaceholder = computed(() => checkDocumento('', '', { passport: true }).example);

  const dniErrorMessage = computed(() => {
    if (!showDocumentacionForm.value) {
      return '';
    }
    if (errors.dni) {
      return errors.dni;
    }
    if (
      !formularioData.formDataDocumentacion.dni ||
      !viaje.formularioInscripcion?.checkIn.paisEnabled ||
      !formularioData.formDataDocumentacion.pais
    ) {
      return '';
    }
    const result = dniFormatResult();
    if (result.isValid) return '';
    return invalidDocumentMessage(result);
  });

  const pasaporteErrorMessage = computed(() => {
    if (!showDocumentacionForm.value || !showPasaporteFields.value) return '';
    if (errors.pasaporte) return errors.pasaporte;
    if (!formularioData.formDataDocumentacion.pasaporte) return '';
    const result = pasaporteFormatResult();
    if (result.isValid) return '';
    return invalidDocumentMessage(result, true);
  });
  function isLongPregunta(texto) {
    return (texto?.length ?? 0) > LONG_PREGUNTA_CHARS;
  }

  function invalidDocumentMessage(result, isPassport = false) {
    const key = isPassport
      ? 'reserva.checkIn.error.documento.pasaporteNotValid'
      : 'reserva.checkIn.error.documento.notValidWithHints';
    return t(key, { hints: result.hints });
  }

  function dniFormatResult() {
    return checkDocumento(formularioData.formDataDocumentacion.pais, formularioData.formDataDocumentacion.dni);
  }

  function pasaporteFormatResult() {
    return checkDocumento(formularioData.formDataDocumentacion.pais, formularioData.formDataDocumentacion.pasaporte, {
      passport: true,
    });
  }

  function clearErrors() {
    Object.keys(errors).forEach(key => {
      errors[key] = key === 'preguntas' ? {} : '';
    });
  }

  function validateFields() {
    clearErrors();
    const requiredMessage = t('reserva.validation.requiredField');

    if (formularioData.checkInPendiente && showDocumentacionForm.value) {
      if (
        viaje.formularioInscripcion.checkIn.nacionalidadEnabled &&
        !formularioData.formDataDocumentacion.nacionalidad
      ) {
        errors.nacionalidad = requiredMessage;
      }
      if (
        viaje.formularioInscripcion.checkIn.fechaNacimientoEnabled &&
        !formularioData.formDataDocumentacion.fechaNacimiento
      ) {
        errors.fechaNacimiento = requiredMessage;
      }
      if (
        viaje.formularioInscripcion.checkIn.lugarNacimientoEnabled &&
        !formularioData.formDataDocumentacion.lugarNacimiento
      ) {
        errors.lugarNacimiento = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.sexoEnabled && !formularioData.formDataDocumentacion.sexo) {
        errors.sexo = requiredMessage;
      }
      if (showTipoDocumentoSelector.value && !formularioData.formDataDocumentacion.tipoDocumento) {
        errors.tipoDocumento = requiredMessage;
      }
      if (showDniFields.value && viaje.formularioInscripcion.checkIn.dniEnabled) {
        if (!formularioData.formDataDocumentacion.dni) {
          errors.dni = requiredMessage;
        } else if (viaje.formularioInscripcion.checkIn.paisEnabled && formularioData.formDataDocumentacion.pais) {
          const result = dniFormatResult();
          if (!result.isValid) {
            errors.dni = invalidDocumentMessage(result);
          }
        }
      }
      if (
        showDniFields.value &&
        viaje.formularioInscripcion.checkIn.anversoDniEnabled &&
        !formularioData.formDataDocumentacion.anversoDni
      ) {
        errors.anversoDni = requiredMessage;
      }
      if (
        showDniFields.value &&
        viaje.formularioInscripcion.checkIn.reversoDniEnabled &&
        !formularioData.formDataDocumentacion.reversoDni
      ) {
        errors.reversoDni = requiredMessage;
      }
      if (showPasaporteFields.value && viaje.formularioInscripcion.checkIn.pasaporteEnabled) {
        if (!formularioData.formDataDocumentacion.pasaporte) {
          errors.pasaporte = requiredMessage;
        } else {
          const result = pasaporteFormatResult();
          if (!result.isValid) {
            errors.pasaporte = invalidDocumentMessage(result, true);
          }
        }
      }
      if (
        showPasaporteFields.value &&
        viaje.formularioInscripcion.checkIn.anversoPasaporteEnabled &&
        !formularioData.formDataDocumentacion.anversoPasaporte
      ) {
        errors.anversoPasaporte = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.paisEnabled && !formularioData.formDataDocumentacion.pais) {
        errors.pais = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.provinciaEnabled && !formularioData.formDataDocumentacion.provincia) {
        errors.provincia = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.poblacionEnabled && !formularioData.formDataDocumentacion.poblacion) {
        errors.poblacion = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.domicilioEnabled && !formularioData.formDataDocumentacion.direccion) {
        errors.direccion = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.codPostalEnabled && !formularioData.formDataDocumentacion.codigoPostal) {
        errors.codigoPostal = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.llegadaEnabled && !formularioData.formDataDocumentacion.horaCheckIn) {
        errors.llegada = requiredMessage;
      }
      if (viaje.formularioInscripcion.checkIn.salidaEnabled && !formularioData.formDataDocumentacion.horaCheckOut) {
        errors.salida = requiredMessage;
      }
    }

    if (formularioData.datosPersonalesPendiente && showPersonalForm.value) {
      if (viaje.formularioInscripcion.datosPersonales.alergiasEnabled && !formularioData.formDataPersonal.alergias) {
        errors.alergias = requiredMessage;
      }
      if (viaje.formularioInscripcion.datosPersonales.alturaEnabled && !formularioData.formDataPersonal.altura) {
        errors.altura = requiredMessage;
      }
      if (viaje.formularioInscripcion.datosPersonales.pesoEnabled && !formularioData.formDataPersonal.peso) {
        errors.peso = requiredMessage;
      }
      if (viaje.formularioInscripcion.datosPersonales.tallaPieEnabled && !formularioData.formDataPersonal.tallaPie) {
        errors.tallaPie = requiredMessage;
      }
    }

    if ((formularioData.preguntasPendiente || !props.instant) && showPreguntas.value) {
      viaje.formularioInscripcion.preguntas.forEach(pregunta => {
        const respuesta = formularioData.formDataPreguntas.preguntas.find(item => item.id === pregunta.id)?.respuesta;
        if (!respuesta) {
          errors.preguntas[pregunta.id] = requiredMessage;
        }
      });
    }

    return (
      !errors.nacionalidad &&
      !errors.fechaNacimiento &&
      !errors.lugarNacimiento &&
      !errors.sexo &&
      !errors.tipoDocumento &&
      !errors.dni &&
      !errors.pasaporte &&
      !errors.pais &&
      !errors.provincia &&
      !errors.poblacion &&
      !errors.direccion &&
      !errors.codigoPostal &&
      !errors.llegada &&
      !errors.salida &&
      !errors.anversoDni &&
      !errors.reversoDni &&
      !errors.anversoPasaporte &&
      !errors.alergias &&
      !errors.altura &&
      !errors.peso &&
      !errors.tallaPie &&
      Object.keys(errors.preguntas).length === 0
    );
  }

  function validateAndShowErrors() {
    submitted.value = true;
    return validateFields();
  }

  function fieldValue(field) {
    const formDocumentacion = formularioData.formDataDocumentacion;
    const formPersonal = formularioData.formDataPersonal;
    return {
      [CAMPOS_FORMULARIO.LUGAR_NACIMIENTO]: formDocumentacion.lugarNacimiento,
      [CAMPOS_FORMULARIO.DNI]: formDocumentacion.dni,
      [CAMPOS_FORMULARIO.PASAPORTE]: formDocumentacion.pasaporte,
      [CAMPOS_FORMULARIO.PROVINCIA]: formDocumentacion.provincia,
      [CAMPOS_FORMULARIO.POBLACION]: formDocumentacion.poblacion,
      [CAMPOS_FORMULARIO.DIRECCION]: formDocumentacion.direccion,
      [CAMPOS_FORMULARIO.CODIGO_POSTAL]: formDocumentacion.codigoPostal,
      [CAMPOS_FORMULARIO.ALERGIAS]: formPersonal.alergias,
      [CAMPOS_FORMULARIO.ALTURA]: formPersonal.altura,
      [CAMPOS_FORMULARIO.PESO]: formPersonal.peso,
      [CAMPOS_FORMULARIO.TALLA_PIE]: formPersonal.tallaPie,
    }[field];
  }

  function validateField(field) {
    errors[field] = !String(fieldValue(field) ?? '').trim() ? t('reserva.validation.requiredField') : '';
  }

  function validatePregunta(preguntaId) {
    const pregunta = formularioData.formDataPreguntas.preguntas.find(item => String(item.id) === String(preguntaId));
    const isEmpty = !pregunta?.respuesta?.trim();

    if (isEmpty) {
      errors.preguntas[preguntaId] = t('reserva.validation.requiredField');
      return;
    }

    delete errors.preguntas[preguntaId];
  }

  async function fileChangeHandler(event, field) {
    const file = event.target.files[0];
    const compressedFile = await comprimirImagen(file, 40);
    if (compressedFile.size > 2097152) {
      toaster.error(`${t('home.form.tamañoMaximo')} 2mb`);
      event.target.value = null;
      return;
    }
    if (compressedFile) {
      formularioData.formDataDocumentacion[field] = new File([compressedFile], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });
    }
  }

  function fileDeletedHandler(field, input) {
    formularioData.formDataDocumentacion[field] = null;
    input.value = null;
  }

  watch(
    formularioData,
    debounce(newValue => {
      emits('updateFormulario', cloneDeep(newValue));
    }, 500)
  );

  watch(
    formularioData,
    () => {
      if (!submitted.value) return;
      validateFields();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    countryList.value = getCountryOptions();
    emits('updateFormulario', cloneDeep(formularioData));
  });

  defineExpose({
    formularioData,
    validateAndShowErrors,
  });
</script>

<style lang="scss" scoped>
  @import '~@/assets/scss/main.scss';

  .formulario-inscripcion {
    background-color: var(--landing-background-color, white);
    color: var(--landing-text-color, inherit);
    font-family: var(--landing-text-font, inherit);
  }

  .input-container {
    .label-input {
      margin-bottom: 0.5rem;
      font-size: 16px;
      font-weight: 500;
      color: var(--landing-text-color, inherit);

      @include media-breakpoint-down(md) {
        font-size: 13px;
      }
    }

    &.input-error {
      .label-input {
        color: var(--bs-danger);
      }

      .form-control,
      .form-select-validate {
        border: solid 2px var(--bs-danger);

        &:focus {
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(255, 10, 30, 0.25);
        }
      }
    }
  }
  .form-label {
    font-weight: 500;
    font-size: 16px;
    color: var(--landing-text-color, var(--bs-primary));
  }

  .form-check-label {
    color: var(--landing-text-color, inherit);
  }

  .card-resumen {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);

    .card-body {
      padding: 1rem 2rem;
    }
  }

  .title-name {
    font-size: 20px;
    font-weight: 600;
    color: var(--landing-text-color, var(--blue-1));
  }

  .container {
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
    padding: 20px;
  }

  .btn-upload {
    padding: 8px 16px;
    background-color: #f0f0f0;
    border: 1px solid var(--landing-text-color, var(--blue-1));
    border-radius: 4px;
    cursor: pointer;
    color: var(--landing-text-color, #333);
  }

  .radio-group {
    display: flex;
    gap: 20px;
  }

  .radio-group label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .datetime-group {
    display: flex;
    gap: 10px;
  }

  .datetime-group input {
    flex: 1;
  }

  .radio-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .radio-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .radio-circle {
    flex: 0 0 auto;
  }

  .radio-circle input[type='radio'] {
    margin: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .radio-option span {
    color: var(--landing-text-color, var(--blue-1));
    font-size: 0.9rem;
  }

  .conditional-input {
    margin: 0.5rem 0 0.5rem 20px;
    width: calc(100% - 20px);
  }

  .conditional-input input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background-color: #f8f8f8;
  }

  .file-input-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
  }

  .file-input {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
  }

  .file-input-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    border: 1px solid var(--landing-text-color, var(--bs-primary));
    border-radius: 25px;
    background: var(--landing-background-color, var(--bs-white));
    color: var(--landing-text-color, var(--bs-primary));
    width: 100%;
    cursor: pointer;
    transition: all 0.3s ease;

    span {
      color: var(--landing-text-color, var(--bs-primary));
    }

    i,
    :deep(svg) {
      color: var(--landing-text-color, var(--bs-primary));
      margin-left: 10px;
    }

    :deep(.upload-icon__path) {
      fill: currentColor;
    }

    &:hover {
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05);
    }
  }

  .file-input-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .file-input-button.is-invalid {
    border: 1px solid var(--bs-danger) !important;
  }

  .form-control,
  .form-select {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    height: 40px !important;
    padding: 0.375rem 0.75rem !important;
    text-align: left !important;
    color: var(--landing-text-color, inherit) !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }
  }

  .input-group-text {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    color: var(--landing-text-color, inherit) !important;
  }

  .form-control.is-invalid {
    border: 1px solid var(--bs-danger) !important;
  }

  :deep(.input-selector-wrapper.is-field-error .form-control) {
    border: 1px solid var(--bs-danger) !important;
  }

  :deep(.input-selector-wrapper .form-control) {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    color: var(--landing-text-color, inherit) !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }
  }

  :deep(.is-invalid .dp__input) {
    border: 1px solid var(--bs-danger) !important;
  }

  :deep(.dp__input) {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    height: 40px !important;
    text-align: start !important;
    color: var(--landing-text-color, inherit) !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }

    @media only screen and (max-width: 768px) {
      font-size: 12px !important;
    }

    &:disabled {
      background-color: var(--landing-background-color, var(--bs-white)) !important;
      opacity: 0.7;
    }
  }

  :deep(.dp__input_icon) {
    color: var(--landing-text-color, var(--bs-primary));
  }

  .form-check {
    .form-check-input[type='radio'] {
      cursor: pointer;
      border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.3);

      &:checked {
        background-color: var(--bs-secondary);
        border-color: var(--landing-text-color, var(--bs-primary));
      }

      &:focus {
        box-shadow: none;
      }
    }
  }

  :deep(.tooltip-left) {
    width: 10vw;

    @media (max-width: 768px) {
      font-size: 11px;
    }
  }

  .form-control,
  .form-select {
    &:disabled {
      background-color: var(--bs-white) !important;
      opacity: 0.7 !important;
    }
  }

  @media screen and (max-width: 768px) {
    .form-label,
    .form-control,
    .form-select,
    .form-check-label,
    input::placeholder,
    select,
    .input-group-text {
      font-size: 13px !important;
    }
  }

  .aviso-documentos {
    border-radius: var(--bs-border-radius-lg);
    padding: 10px 14px;
    border: 1px solid rgba(var(--landing-detail-color-rgb, var(--bs-secondary-rgb)), 0.4);
    background-color: rgba(var(--landing-detail-color-rgb, var(--bs-secondary-rgb)), 0.05);
  }
</style>
